import React, { useState, useEffect } from "react";
import Table from "../../../core/common/dataTable/index";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import {
  getAllSessions,
  createSession,
  updateSession,
  getSessionScopes,
  setSessionScopes,
  type Session,
  type CreateSessionData,
  type UpdateSessionData,
  type SessionScope,
} from "../../../service/sessions";
import { getAllClasses } from "../../../service/classes";
import { getAllSystems } from "../../../service/systems";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { setSessions as setSessionsInRedux } from "../../../core/data/redux/slice/sessionsSlice";
import { selectBranches } from "../../../core/data/redux/slice/branchesSlice";

type ClassItem = { _id: string; name: string; system?: string };
type SystemItem = { _id: string; name: string };

// One row per class -- every class belonging to this session's campus gets
// its own explicit start/end date, not just the ones that differ from the
// session's own. Defaults to the session's own dates; editing any of them
// is what actually creates an override (client's own call, 2026-10-08:
// setting a date per class rather than only flagging exceptions).
type ClassDateRow = {
  classId: string;
  className: string;
  startDate: string;
  endDate: string;
};

const SessionManagement = () => {
  const routes = all_routes;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pagination, setPagination] = useState({
    limit: 10,
    page: 1,
  });
  const [paginationData, setPaginationData] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 10,
  });
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [formData, setFormData] = useState<CreateSessionData>({
    title: "",
    startDate: "",
    endDate: "",
    status: "active",
  });

  // ── Class Dates modal: every class belonging to this session's campus
  // gets its own explicit start/end date row, defaulting to the session's
  // own dates -- client's own call (2026-10-08), rather than only listing
  // exceptions. Still backed by the same SessionScope rows under the hood:
  // Enrollment, head count, attendance and invoices keep pointing at this
  // exact session, unchanged -- only the handful of places that need a
  // specific class's real calendar read these per-class dates.
  const branches = useSelector(selectBranches);
  const [allClasses, setAllClasses] = useState<ClassItem[]>([]);
  const [allSystems, setAllSystems] = useState<SystemItem[]>([]);
  const [scopingSession, setScopingSession] = useState<Session | null>(null);
  const [scopeRows, setScopeRows] = useState<ClassDateRow[]>([]);
  const [showBoardLabel, setShowBoardLabel] = useState(false);
  const [scopesLoading, setScopesLoading] = useState(false);
  const [savingScopes, setSavingScopes] = useState(false);

  const openScopesModal = async (session: Session) => {
    setScopingSession(session);
    setScopesLoading(true);
    try {
      const [existingScopes, classesRes, systemsRes] = await Promise.all([
        getSessionScopes(session._id),
        allClasses.length ? Promise.resolve(null) : getAllClasses({ limit: 500, all: "true" }),
        allSystems.length ? Promise.resolve(null) : getAllSystems(),
      ]);

      const classesList: ClassItem[] = classesRes
        ? ((classesRes as any).classes || []).map((c: any) => ({
            _id: c._id,
            name: c.name,
            system: c.system?._id || c.system,
          }))
        : allClasses;
      if (classesRes) setAllClasses(classesList);

      const systemsList: SystemItem[] = systemsRes ? (systemsRes as any).data || [] : allSystems;
      if (systemsRes) setAllSystems(systemsList);

      // A branch can run more than one board (e.g. Naseerabad's Matric +
      // AKU), and each board has its OWN "Class IX"/"Class X" document --
      // same name, different class, different subjects. Scoped to this
      // session's own branch so classes from other campuses never show up,
      // labelled by board whenever that branch runs more than one.
      const branchId = typeof session.branch === "string" ? session.branch : session.branch?._id;
      const branch: any = branches.find((b) => b._id === branchId);
      const allowedSystemIds = (branch?.systems || []).map((s: any) => String(s._id || s));
      const applicable = allowedSystemIds.length
        ? classesList.filter((c) => c.system && allowedSystemIds.includes(c.system))
        : classesList;
      const multiBoard = allowedSystemIds.length > 1;
      setShowBoardLabel(multiBoard);
      const systemNameById = new Map(systemsList.map((s) => [s._id, s.name]));

      const existingByClass = new Map(
        existingScopes.flatMap((s: SessionScope) =>
          s.classes.map((c) => [
            c._id,
            { startDate: s.startDate.split("T")[0], endDate: s.endDate.split("T")[0] },
          ])
        )
      );
      const sessionStart = session.startDate.split("T")[0];
      const sessionEnd = session.endDate.split("T")[0];

      setScopeRows(
        applicable.map((c) => {
          const existing = existingByClass.get(c._id);
          return {
            classId: c._id,
            className: multiBoard && c.system ? `${c.name} — ${systemNameById.get(c.system) || ""}` : c.name,
            startDate: existing?.startDate || sessionStart,
            endDate: existing?.endDate || sessionEnd,
          };
        })
      );
    } catch {
      toast.error("Error loading class dates");
    } finally {
      setScopesLoading(false);
    }
  };

  const closeScopesModal = () => {
    const el = document.getElementById("class_dates_modal");
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal");
      el.click();
      el.removeAttribute("data-bs-dismiss");
    }
    setScopingSession(null);
    setScopeRows([]);
  };

  const updateScopeDate = (classId: string, field: "startDate" | "endDate", value: string) => {
    setScopeRows((prev) => prev.map((r) => (r.classId === classId ? { ...r, [field]: value } : r)));
  };

  const saveScopes = async () => {
    if (!scopingSession) return;
    for (const row of scopeRows) {
      if (!row.startDate || !row.endDate) {
        toast.error(`${row.className}: both a start and end date are required`);
        return;
      }
      if (new Date(row.endDate) <= new Date(row.startDate)) {
        toast.error(`${row.className}: end date must be after start date`);
        return;
      }
    }
    setSavingScopes(true);
    try {
      await setSessionScopes(
        scopingSession._id,
        scopeRows.map((r) => ({
          classes: [r.classId],
          startDate: r.startDate,
          endDate: r.endDate,
        }))
      );
      toast.success("Class dates saved");
      closeScopesModal();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Error saving class dates");
    } finally {
      setSavingScopes(false);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    try {
      const response = await getAllSessions({
        ...pagination,
      });
      setSessions(response.sessions);
      dispatch(setSessionsInRedux(response.sessions));
      setPaginationData(response.pagination);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Error fetching sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [pagination]);

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const dataToSend = {
        title: formData.title,
        startDate: formData.startDate,
        endDate: formData.endDate,
        isActive: formData.status === "active",
      };

      await createSession(dataToSend);
      toast.success("Session created successfully!");
      fetchSessions();
      const el = document.getElementById("add_session_modal");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      setFormData({
        title: "",
        startDate: "",
        endDate: "",
        status: "active",
      });
    } catch (error: any) {
      console.error("Error creating session:", error);
      toast.error(error.response?.data?.message || "Error creating session");
    }
  };

  const handleUpdateSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSession) return;

    try {
      const updateData: UpdateSessionData = {};
      if (formData.title !== undefined) updateData.title = formData.title;
      if (formData.startDate !== undefined)
        updateData.startDate = formData.startDate;
      if (formData.endDate !== undefined) updateData.endDate = formData.endDate;
      if (formData.status !== undefined) updateData.status = formData.status;

      await updateSession(editingSession._id, updateData);
      toast.success("Session updated successfully!");
      const el = document.getElementById("edit_session_modal");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
      fetchSessions();
      setEditingSession(null);
      setFormData({
        title: "",
        startDate: "",
        endDate: "",
        status: "active",
      });
    } catch (error: any) {
      console.error("Error updating session:", error);
      toast.error(error.response?.data?.message || "Error updating session");
    }
  };

  const handleChange = (page: number, pageSize: number) => {
    setPagination({ limit: pageSize, page: page });
  };

  const handleDeactivateSession = async (id: string) => {
    if (!id || isProcessing) return;
    setIsProcessing(true);
    try {
      await updateSession(id, { status: "inactive" });
      toast.success("Session deactivated successfully!");
      fetchSessions();
      const el = document.getElementById("deactivate_modal");
      if (el) {
        el.setAttribute("data-bs-dismiss", "modal");
        el.click();
        el.removeAttribute("data-bs-dismiss");
      }
    } catch (error: any) {
      console.error("Error deactivating session:", error);
      toast.error(
        error.response?.data?.message || "Error deactivating session"
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleActivateSession = async (session: Session) => {
    try {
      await updateSession(session._id, { status: "active" });
      toast.success("Session activated successfully!");
      fetchSessions();
    } catch (error: any) {
      console.error("Error activating session:", error);
      toast.error(error.response?.data?.message || "Error activating session");
    }
  };

  const handleToggleopenForRegistration = async (session: Session) => {
    try {
      await updateSession(session._id, {
        openForRegistration: !session.openForRegistration,
      });
      toast.success(
        `Registration ${!session.openForRegistration ? "opened" : "closed"} for ${session.title}`
      );
      fetchSessions();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to update session");
    }
  };

  const handleDeactivateClick = (session: Session) => {
    const currentStatus = getSessionStatus(session);
    if (currentStatus === "inactive") {
      toast.info("Session is already inactive");
      return;
    }
    setEditingSession(session);
  };

  const handleConfirmDeactivate = (e: React.MouseEvent) => {
    e.preventDefault();
    if (editingSession) {
      handleDeactivateSession(editingSession._id);
    }
  };

  const openEditModal = (session: Session) => {
    setEditingSession(session);
    setFormData({
      title: session.title,
      startDate: session.startDate.split("T")[0],
      endDate: session.endDate.split("T")[0],
      status: getSessionStatus(session),
    });
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSessionClick = (session: Session) => {
    navigate(`/academic/term-management/${session._id}`);
  };

  const getSessionStatus = (session: Session): string => {
    if (session.status) {
      return session.status;
    }
    return session.isActive ? "active" : "inactive";
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      sorter: true,
      render: (text: string, record: Session) => (
        <Link
          to="#"
          className="text-primary"
          onClick={(e) => {
            e.preventDefault();
            handleSessionClick(record);
          }}
          style={{ cursor: "pointer", textDecoration: "underline" }}
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Start Date",
      dataIndex: "startDate",
      render: (text: string) => {
        try {
          return new Date(text).toLocaleDateString("en-IN");
        } catch {
          return text;
        }
      },
      sorter: true,
    },
    {
      title: "End Date",
      dataIndex: "endDate",
      render: (text: string) => {
        try {
          return new Date(text).toLocaleDateString("en-IN");
        } catch {
          return text;
        }
      },
      sorter: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text: string, record: Session) => {
        const status = getSessionStatus(record);
        return (
          <span
            className={`badge badge-soft-${
              status === "active" ? "success" : "danger"
            } d-inline-flex align-items-center`}
          >
            <i className={`ti ti-circle-filled fs-5 me-1`}></i>
            {status === "active" ? "Active" : "Inactive"}
          </span>
        );
      },
      sorter: true,
    },
    {
      title: "Open for Registration",
      dataIndex: "openForRegistration",
      render: (_: any, record: Session) => (
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            checked={!!record.openForRegistration}
            onChange={() => handleToggleopenForRegistration(record)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (text: string, record: Session) => {
        const status = getSessionStatus(record);
        return (
          <div className="d-flex align-items-center">
            <div className="dropdown">
              <Link
                to="#"
                className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                data-bs-toggle="dropdown"
                data-bs-boundary="viewport"
                data-bs-popper-config='{"strategy":"fixed"}'
                aria-expanded="false"
              >
                <i className="ti ti-dots-vertical fs-14" />
              </Link>
              <ul className="dropdown-menu dropdown-menu-right p-3">
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#edit_session_modal"
                    onClick={() => openEditModal(record)}
                  >
                    <i className="ti ti-edit-circle me-2" />
                    Edit
                  </Link>
                </li>
                <li>
                  <Link
                    className="dropdown-item rounded-1"
                    to="#"
                    data-bs-toggle="modal"
                    data-bs-target="#class_dates_modal"
                    onClick={() => openScopesModal(record)}
                  >
                    <i className="ti ti-calendar-event me-2" />
                    Class Dates
                  </Link>
                </li>
                {status === "active" ? (
                  <li>
                    <Link
                      className="dropdown-item rounded-1 text-danger"
                      to="#"
                      data-bs-toggle="modal"
                      data-bs-target="#deactivate_modal"
                      onClick={() => handleDeactivateClick(record)}
                    >
                      <i className="ti ti-ban me-2" />
                      Deactivate
                    </Link>
                  </li>
                ) : (
                  <li>
                    <button
                      className="dropdown-item rounded-1 text-success"
                      onClick={() => handleActivateSession(record)}
                    >
                      <i className="ti ti-check me-2" />
                      Activate
                    </button>
                  </li>
                )}
              </ul>
            </div>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Session Management</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Academic</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Sessions
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="mb-2">
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-toggle="modal"
                  data-bs-target="#add_session_modal"
                  onClick={() => {
                    setEditingSession(null);
                    setFormData({
                      title: "",
                      startDate: "",
                      endDate: "",
                      status: "active",
                    });
                  }}
                >
                  <i className="ti ti-square-rounded-plus-filled me-2" />
                  Add Session
                </Link>
              </div>
            </div>
          </div>

          {/* Sessions List */}
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
              <h4 className="mb-3">Sessions</h4>
            </div>
            <div className="card-body p-0 py-3">
              <Table
                dataSource={sessions}
                columns={columns}
                loading={loading}
                pagination={{
                  ...paginationData,
                  onChange: handleChange,
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add Session Modal */}
      <div className="modal fade" id="add_session_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Add Session</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleCreateSession}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Session Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Add Session
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Edit Session Modal */}
      <div className="modal fade" id="edit_session_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">Edit Session</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form onSubmit={handleUpdateSession}>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-12">
                    <div className="mb-3">
                      <label className="form-label">Session Title</label>
                      <input
                        type="text"
                        className="form-control"
                        name="title"
                        value={formData.title}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Start Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="startDate"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">End Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="endDate"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Deactivate Modal */}
      <div className="modal fade" id="deactivate_modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body text-center">
              <span className="delete-icon">
                <i className="ti ti-ban fs-1 text-warning" />
              </span>
              <h4>Confirm Deactivation</h4>
              <p>
                You want to deactivate this session. This will mark the session
                as inactive.
              </p>
              <div className="d-flex justify-content-center">
                <button
                  className="btn btn-light me-3"
                  data-bs-dismiss="modal"
                  disabled={isProcessing}
                >
                  Cancel
                </button>
                <button
                  id="deactivate_modal_confirm"
                  className="btn btn-warning"
                  onClick={handleConfirmDeactivate}
                  disabled={isProcessing}
                >
                  {isProcessing ? "Deactivating..." : "Yes, Deactivate"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Class Dates Modal -- every class gets its own explicit start/end
          date, defaulting to this session's own dates (e.g. most of them
          stay "Aug 2026-2027", but Matric Class IX/X genuinely run Apr-Mar
          and their rows get edited to say so). This never creates a second
          session; it only overrides which dates count as valid for a
          specific class, read only where that actually matters (exam
          dates, fee billing). Enrollments, head count, attendance and
          invoices keep pointing at this exact session, unchanged. */}
      <div className="modal fade" id="class_dates_modal">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">
                Class Dates{scopingSession ? ` — ${scopingSession.title}` : ""}
              </h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={closeScopesModal}
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <p className="text-muted small mb-3">
                Every class starts out with this session's own dates. Change a row only
                if that class's real academic year actually runs differently (e.g.
                Matric Class IX/X starting in April).
              </p>
              {scopesLoading ? (
                <div className="text-center text-muted py-4">Loading…</div>
              ) : scopeRows.length === 0 ? (
                <p className="small text-muted mb-0">
                  No classes found for this session's campus.
                </p>
              ) : (
                <div className="table-responsive" style={{ maxHeight: 420, overflowY: "auto" }}>
                  <table className="table table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Class</th>
                        <th style={{ width: 170 }}>Start Date</th>
                        <th style={{ width: 170 }}>End Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scopeRows.map((row) => (
                        <tr key={row.classId}>
                          <td className="small">{row.className}</td>
                          <td>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={row.startDate}
                              onChange={(e) => updateScopeDate(row.classId, "startDate", e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              type="date"
                              className="form-control form-control-sm"
                              value={row.endDate}
                              onChange={(e) => updateScopeDate(row.classId, "endDate", e.target.value)}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-light me-2" onClick={closeScopesModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={saveScopes}
                disabled={savingScopes || scopesLoading || scopeRows.length === 0}
              >
                {savingScopes ? "Saving…" : "Save class dates"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionManagement;
